/** @format */

import React, { useRef, useEffect, useCallback, Suspense, lazy } from "react";
import { Helmet } from "react-helmet-async";
import "../../styles/home.css";
import ExploreApartments from "../../components/PropertyTypeSection";
import { IconButton, Flex, Box, Text } from "@chakra-ui/react";
import { ArrowUpIcon } from "@chakra-ui/icons";
import ExploreTowns from "../../components/ExploreTowns";
import CallToAction from "../../components/Home/CallToAction";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { FormattedMessage } from "react-intl";
import SearchBar from "../../components/Home/SearchBar";
import ScrollReveal from "../../components/common/ScrollReveal";
import { navigationSession } from "../../utils/constant";
const Property = lazy(() => import("../../components/Home/Property"));
const Feature = lazy(() => import("../../components/Home/Feature"));
const Blogs = lazy(() => import("../../components/Home/Blogs"));
const Testimonial = lazy(() =>
  import("../../components/Testimonials/Testimonial")
);


const Home = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    name: "Sandseekers",
    url: "https://www.sandseekers.ae/",
    description:
      "Premium real estate services in UAE specializing in luxury properties in Dubai and Abu Dhabi",
    address: {
      "@type": "PostalAddress",
      addressCountry: "AE",
      addressRegion: "Dubai",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+971-02-6277156",
      contactType: "customer service",
      availableLanguage: ["English", "Arabic"],
    },
    sameAs: [
      "https://www.facebook.com/profile.php?id=61560022119418",
      "https://www.linkedin.com/company/sand-seekers-real-estate-llc/",
      "https://www.instagram.com/sandseekersrealestatellc/",
    ],
    areaServed: ["Dubai", "Abu Dhabi", "UAE"],
  };

  const location = useLocation();
  const exploreTownsRef = useRef(null);
  const scrollButtonRef = useRef(null);

  // Handle scroll recovery from specific sections (e.g., when going back from property details)
  useEffect(() => {
    // 1. If this is a fresh entry or hard refresh, navigationSession.isStarted will be false.
    // We prioritize scrolling to the top and marking the session as active.
    if (!navigationSession.isStarted) {
      window.scrollTo(0, 0);
      navigationSession.isStarted = true;
      return;
    }

    // 2. If the session is already active (internal navigation), we restore the scroll position
    // if a section ID has been saved in the history state.
    if (location.state?.fromSection) {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }

      // Use a recursive retry to ensure target elements are in the DOM (handles lazy loading)
      const scrollToSection = () => {
        const element = document.getElementById(location.state.fromSection);
        if (element) {
          element.scrollIntoView({ behavior: "instant", block: "start" });
          return true;
        }
        return false;
      };

      if (!scrollToSection()) {
        const timer = setTimeout(scrollToSection, 150);
        return () => clearTimeout(timer);
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (
      location.state?.scrollTo === "ExploreTowns" &&
      exploreTownsRef.current
    ) {
      exploreTownsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.state]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleScroll = useCallback(() => {
    if (!scrollButtonRef.current) return;

    if (window.scrollY > 200) {
      scrollButtonRef.current.style.display = "flex";
    } else {
      scrollButtonRef.current.style.display = "none";
    }
  }, []);

  useEffect(() => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => {
        if (scrollButtonRef.current) {
          scrollButtonRef.current.style.display =
            window.scrollY > 200 ? "flex" : "none";
        }
      });
    } else {
      setTimeout(() => {
        if (scrollButtonRef.current) {
          scrollButtonRef.current.style.display =
            window.scrollY > 200 ? "flex" : "none";
        }
      }, 100);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  let Language;
  const lang = localStorage.getItem("lang");
  if (lang) {
    Language = "ar";
  } else {
    Language = "en";
  }
  return (
    <>
      <Helmet>
        <title>Sandseekers - Premium Real Estate Services in UAE</title>
        <meta
          name="description"
          content="Discover luxury properties in Dubai & Abu Dhabi with Sandseekers. Expert real estate services, exclusive listings, and personalized property solutions in the UAE."
        />
        <meta
          name="keywords"
          content="UAE real estate, Dubai properties, Abu Dhabi homes, luxury villas, apartments for sale, property investment"
        />
        <meta
          property="og:title"
          content="Sandseekers - Premium Real Estate Services in UAE"
        />
        <meta
          property="og:description"
          content="Discover luxury properties in Dubai & Abu Dhabi with Sandseekers. Expert real estate services, exclusive listings, and personalized property solutions in the UAE."
        />
        <link rel="canonical" href="https://www.sandseekers.ae/" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Sandseekers UAE - Premium Real Estate Services"
        />
        <meta
          property="og:description"
          content="Discover luxury properties in Dubai & Abu Dhabi with Sandseekers. Expert real estate services and exclusive listings in UAE."
        />
        <meta property="og:url" content="https://www.sandseekers.ae/" />
        <meta property="og:site_name" content="Sandseekers" />
        <meta
          property="og:image"
          content="https://www.sandseekers.ae/images/sandseekers-social-share.jpg"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        <meta
          name="twitter:title"
          content="Sandseekers UAE - Premium Real Estate Services"
        />
        <meta
          name="twitter:description"
          content="Discover luxury properties in Dubai & Abu Dhabi with Sandseekers. Expert real estate services and exclusive listings."
        />

        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <Box>
        {/* Header is now handled with absolute positioning in its own component for home page */}
        <div className="header-wrapper" style={{ background: "transparent", border: "none" }}>
          <Header />
        </div>

        <div className="home-section-1">
          <Box
            position="relative"
            pt={{ base: "140px", md: "180px", lg: "200px" }}
            pb={{ base: "100px", md: "150px" }}
            px={{ base: 4, md: "60px" }}
            overflow="visible"
            zIndex={10}
          >

            <Box position="relative" zIndex={1} textAlign="left" color="white" maxW="1200px">
              <Text
                fontSize={{ base: "xs", md: "sm", lg: "md" }}
                fontWeight="600"
                letterSpacing="6px"
                mb={6}
                color="rgba(255, 255, 255, 0.9)"
                textTransform="uppercase"
              >
                <FormattedMessage id="your_luxury_residence" />
              </Text>

              <Text
                as="h1"
                fontSize={{ base: "36px", md: "64px", lg: "86px" }}
                fontWeight="800"
                lineHeight={{ base: "1.2", md: "1.1" }}
                mb={12}
                maxW="1000px"
                style={{
                  textShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  letterSpacing: "-1px",
                }}
              >
                <FormattedMessage id="find_perfect_place" />
              </Text>

              <Box mt={10}>
                <SearchBar />
              </Box>
            </Box>
          </Box>
        </div>

        <Box px={0}>
          <Box id="latest-properties-section" minH={{ base: "600px", md: "800px" }}>
            <Suspense fallback={<Box minH="600px">Loading Property Component...</Box>}>
              <ScrollReveal>
                <Property />
              </ScrollReveal>
            </Suspense>
          </Box>

          <Box id="explore-apartments-section" minH="500px">
            <ScrollReveal>
              <ExploreApartments />
            </ScrollReveal>
          </Box>

          <div style={{ padding: 0 }} ref={exploreTownsRef}>
            <ScrollReveal>
              <ExploreTowns />
            </ScrollReveal>
          </div>

          <ScrollReveal>
            <CallToAction />
          </ScrollReveal>

          <Suspense fallback={<div>Loading Feature Component...</div>}>
            <ScrollReveal>
              <Feature />
            </ScrollReveal>

            <ScrollReveal>
              <Testimonial />
            </ScrollReveal>

            <ScrollReveal>
              <Blogs />
            </ScrollReveal>
          </Suspense>

          <div ref={scrollButtonRef} style={{ display: "none" }}>
            <Flex
              position="fixed"
              bottom="30px"
              right="80px"
              zIndex="1000"
              align="center"
              justify="center"
            >
              <IconButton
                icon={<ArrowUpIcon />}
                onClick={scrollToTop}
                colorScheme="yellow"
                aria-label="Scroll to top"
              />
            </Flex>
          </div>
        </Box>
      </Box>
    </>
  );
};

export default Home;
